<template>
  <div class="playback-insight-list-container">
    <div class="page-header">
      <h1 class="title">
        <i class="fas fa-lightbulb icon"></i> Playback Insight
      </h1>
    </div>
    <div class="playback-insight-list">
      <Section>
        <div class="section-body">
          <TextInput
            :name="'id'"
            :placeholder="'Search by Playback ID, User ID, Client ID'"
            :value="client.id"
            :customClass="createInputClass(error, 'title')"
            @change="onChange"
          />
          <div class="playback-insight-list-sorting mt-3">
            <span class="mr-2">FILTER BY:</span>
            <div
              class="tab-item no-link"
              :class="{ 'active': client.filter === 'timestamp' }"
              @click="onClickSort('timestamp')"
            >Timestamp</div>
            <div
              class="tab-item no-link"
              :class="{ 'active': client.filter === 'buffer' }"
              @click="onClickSort('buffer')"
            >Buffering</div>
            <div
              class="tab-item no-link"
              :class="{ 'active': client.filter === 'error' }"
              @click="onClickSort('error')"
            >Error</div>
          </div>
          <div class="playback-insight-list-loading text-center" v-if="state.isLoading">
            <div class="spinner-border" role="status" />
            <h3 class="mt-4">กำลังโหลดลิสต์ Playback...</h3>
          </div>
          <div v-if="!state.isLoading">
            <router-link
              class="mt-4"
              v-for="(playback, index) in client.playbacks"
              :to="`playbacks/${playback.playbackId}`"
              :key="index + playback.playbackId"
            >
              <PlaybackInsightItem :playback="playback" />
            </router-link>
          </div>
          <div
            class="playback-insight-list-loading text-center"
            v-if="!state.isLoading && playbackEmpty"
          >
            <h1>
              <i class="fa fa-sad-tear" />
            </h1>
            <h3 class="mt-4">ไม่พบข้อมูล Playback {{ filterName }}</h3>
            <div
              class="small mt-2"
            >Client ID อาจไม่ถูกต้อง หรือผู้ใช้งานไม่มี Playback ในช่วงเวลานี้</div>
          </div>
        </div>
      </Section>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { getClientPlaybacks } from '@/playback/api';
import parserFormData from '@/utils/formUtils';
import Section from '@/components/Section.vue';
import TextInput from '@/form/TextInput.vue';
import PlaybackInsightItem from '@/playback/PlaybackInsightItem.vue';

export default {
  mixins: [parserFormData],
  components: {
    Section,
    TextInput,
    PlaybackInsightItem,
  },
  data() {
    return {
      // NOTE: Keep this for testing my dude :)
      // playback: {
      //   playbackId: 'cfb5df30-4d40-11ea-b857-cf52a346f428',
      //   clientTimestamp: '2020-02-12T02:38:54.632+00:00',
      //   bufferCount: 1,
      //   errorCount: 0,
      //   playerName: 'jwplayer',
      //   playerVersion: '8.12.4',
      //   deviceFamily: 'Other',
      //   osFamily: 'Windows',
      //   osVersion: 7,
      //   uaFamily: 'Firefox',
      //   uaVersion: 72.0,
      //   count: 44,
      //   bandwidthEstimate: 35.62124458298432,
      // },
      client: {
        id: '',
        playbacks: [],
        filter: 'timestamp',
      },
      error: '',
      state: {
        isLoading: false,
      },
    };
  },
  computed: {
    playbackEmpty() {
      return _.isEmpty(this.client.playbacks);
    },
    clientIdEmpty() {
      return _.isEmpty(this.client.id);
    },
    filterName() {
      let translate = '';
      switch (this.client.filter) {
        case 'timestamp':
          translate = 'Timestamp';
          break;
        case 'buffer':
          translate = 'Buffering';
          break;
        case 'error':
          translate = 'Error';
          break;
        default:
          translate = '';
      }
      return translate;
    },
  },
  created() {
    this.loadRecentPlaybacks();
  },
  methods: {
    loadPlaybacks() {
      if (!this.clientIdEmpty) {
        this.loadClientPlaybacks();
      } else {
        this.loadRecentPlaybacks();
      }
    },
    async loadRecentPlaybacks() {
      this.state.isLoading = true;
      try {
        const queryParams = {
          filter: this.client.filter,
          order: 'desc',
        };
        const response = await getClientPlaybacks(queryParams);
        this.client.playbacks = response.data.playbackIds;
        this.state.isLoading = false;
      } catch (e) {
        this.client.playbacks = [];
        this.error = e;
        this.state.isLoading = false;
      }
    },
    async loadClientPlaybacks() {
      this.state.isLoading = true;
      try {
        const queryParams = {
          q: this.client.id,
          filter: this.client.filter,
          order: 'desc',
        };
        const response = await getClientPlaybacks(queryParams);
        this.client.playbacks = response.data.playbackIds;
        this.state.isLoading = false;
      } catch (e) {
        this.client.playbacks = [];
        this.error = e;
        this.state.isLoading = false;
      }
    },
    onChange(event) {
      const { name, value } = event.target;
      Object.assign(this.client, { [name]: value });
      this.loadPlaybacks();
    },
    onClickSort(filter) {
      this.client.filter = filter;
      this.loadPlaybacks();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';
@import '@/assets/scss/themes/components/page.scss';

a {
  text-decoration: none;
  color: inherit;
}

.tab-item {
  display: inline-block;
  padding: $spacer * 0.25 $spacer * 0.5;
  border-radius: 15px;
  margin: 0 $spacer * 0.25;
  cursor: pointer;
  background-color: white;
  // color: lighten($body-color, 5%);
  color: $gray-700;
  border: 1px solid $gray-300;

  &.active {
    background-color: $secondary;
    color: $white;
  }
  &:hover {
    border-color: $secondary;
  }
  &:first-child {
    margin-left: 0;
  }
}

.playback-insight-list-loading {
  font-weight: normal;
  margin: $spacer * 2.5;
}

.playback-insight-list__empty-state {
  margin: ($spacer * 5) auto;
}
</style>
